/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact/hooks';
import { mergeProps } from '../../utils/UNSAFE_mergeProps';
import { useDragPan } from '../../PRIVATE_TimeComponent/hooks/useDragPan';
import { useWheelPan } from '../../PRIVATE_TimeComponent/hooks/useWheelPan';
import { usePinchZoom } from '../../PRIVATE_TimeComponent/hooks/usePinchZoom';
import { useWheelZoom } from '../../PRIVATE_TimeComponent/hooks/useWheelZoom';
import { useKbZoom } from '../../PRIVATE_TimeComponent/hooks/useKbZoom';
import { useGanttKbPan } from './useGanttKbPan';
import { getISOString } from '../../PRIVATE_TimeComponent/utils/timeUtils';
import {
  OnTimeComponentViewportChange,
  TimeComponentScale,
  TimeComponentZoomParameters
} from '../../PRIVATE_TimeComponent/TimeComponent.types';
import { GanttRow, GanttTask, OnGanttScrollPositionChange } from '../Gantt.types';
import type { ViewportLayout } from '../utils/dataLayoutUtils';

type UsePanZoomGestureOptions<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>> = {
  captureTargetRef: Ref<HTMLDivElement>;
  width: number;
  height: number;
  viewportStartTime: number;
  viewportEndTime: number;
  majorAxisScale?: TimeComponentScale;
  minorAxisScale: TimeComponentScale;
  startTime: number;
  endTime: number;
  zoomParameters: TimeComponentZoomParameters;
  scrollPosition: number;
  dataBodyHeight: number;
  totalRowsHeight: number;
  viewportLayout: ViewportLayout<K1, K2, D1, D2>;
  onViewportChange?: OnTimeComponentViewportChange;
  onScrollPositionChange?: OnGanttScrollPositionChange;
  onCursorChange?: (detail: { cursor: 'grabbing' | 'grabbingUnset' }) => void;
  isPanDisabled?: boolean;
  isZoomDisabled?: boolean;
};

/**
 * Hook that handles pan/zoom related gestures on a Gantt.
 */
const useGanttPanZoomGestures = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>({
  captureTargetRef,
  width,
  height,
  viewportStartTime,
  viewportEndTime,
  majorAxisScale,
  minorAxisScale,
  startTime,
  endTime,
  zoomParameters,
  scrollPosition,
  dataBodyHeight,
  totalRowsHeight,
  viewportLayout,
  onViewportChange,
  onScrollPositionChange,
  onCursorChange,
  isPanDisabled,
  isZoomDisabled
}: UsePanZoomGestureOptions<K1, K2, D1, D2>) => {
  const panOptions = {
    captureTargetRef,
    width,
    height,
    getRangeX: () => ({ min: viewportStartTime, max: viewportEndTime }),
    getRangeY: () => ({ min: scrollPosition, max: scrollPosition + dataBodyHeight }),
    globalRangeX: { min: startTime, max: endTime },
    globalRangeY: { min: 0, max: totalRowsHeight },
    onRangeXChange: (newRange: { min: number; max: number }) => {
      onViewportChange?.({
        viewportStart: getISOString(newRange.min),
        viewportEnd: getISOString(newRange.max),
        majorAxisScale,
        minorAxisScale
      });
    },
    onRangeYChange: (newRange: { min: number; max: number }) => {
      onScrollPositionChange?.({
        y: newRange.min
      });
    },
    isDisabled: isPanDisabled
  };

  const { getZoomOrderViewDurationThresholds, ...restZoomParameters } = zoomParameters;
  const zoomOrderViewDurationThresholds = getZoomOrderViewDurationThresholds(width);
  const zoomOptions = {
    captureTargetRef,
    width,
    ...restZoomParameters,
    zoomOrderViewDurationThresholds,
    onViewportChange,
    isDisabled: isZoomDisabled
  };

  const dragPanProps = useDragPan({
    ...panOptions,
    onDragPanStart: () => onCursorChange?.({ cursor: 'grabbing' }),
    onDragPanEnd: () => onCursorChange?.({ cursor: 'grabbingUnset' })
  });
  const wheelPanProps = useWheelPan(panOptions);
  const kbPanProps = useGanttKbPan<K1, K2, D1, D2>({
    ...panOptions,
    viewportLayout,
    dataBodyWidth: width,
    dataBodyHeight,
    viewportStartTime,
    viewportEndTime,
    scrollPosition
  });
  const pinchZoomProps = usePinchZoom(zoomOptions);
  const wheelZoomProps = useWheelZoom(zoomOptions);
  const kbZoomProps = useKbZoom(zoomOptions);
  const gestureProps = mergeProps(
    dragPanProps,
    wheelPanProps,
    kbPanProps,
    pinchZoomProps,
    wheelZoomProps,
    kbZoomProps
  );

  return { gestureProps };
};

export { useGanttPanZoomGestures };
